import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheckCircle, faTimesCircle, faDatabase, faCode, faTrash, faCopy, faExchangeAlt, faSync } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "~/components";
import systemAuditLogApi from '~/api/systemAuditLogApi';

function HistoryTab() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchHistory();
    }, []);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const res = await systemAuditLogApi.getLogsPaged(1, 50, "", "", "", "DatabaseManagement", null, "", "");
            if (res.data?.Succeeded || res.data?.succeeded) {
                const dataList = res.data.Results || res.data.results || [];
                setLogs(dataList);
            }
        } catch (error) {
            console.error("Failed to fetch history logs", error);
        }
        setLoading(false);
    };

    const parsePayload = (payloadStr) => {
        try {
            return JSON.parse(payloadStr);
        } catch {
            return { raw: payloadStr };
        }
    };

    const getActionBadge = (actionType) => {
        switch (actionType) {
            case 'INSERT': return <span className="badge bg-success">INSERT</span>;
            case 'UPDATE': return <span className="badge bg-warning text-dark">UPDATE</span>;
            case 'DELETE': return <span className="badge bg-danger">DELETE</span>;
            case 'TRANSFER': return <span className="badge bg-info text-dark">TRANSFER</span>;
            case 'EXECUTE': return <span className="badge bg-dark text-white">EXECUTE</span>;
            default: return <span className="badge bg-secondary">{actionType}</span>;
        }
    };

    const renderActionDetails = (row) => {
        let payload = parsePayload(row.Payload);
        if (payload.raw) {
            return <div className="text-muted fs-9 text-truncate" style={{ maxWidth: '250px' }}>{payload.raw}</div>;
        }

        // Unwrap if payload is wrapped inside a parameter name like {"request": {...}}
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
            const keys = Object.keys(payload);
            if (keys.length === 1 && typeof payload[keys[0]] === 'object' && payload[keys[0]] !== null) {
                payload = payload[keys[0]];
            }
        }

        // Determine specific action based on payload fields
        let actionName = row.ActionType;
        let sourceDb = payload.SourceDatabase || payload.sourceDatabase;
        let targetDb = payload.TargetDatabase || payload.targetDatabase || payload.DatabaseName || payload.databaseName;
        let objectName = payload.ObjectName || payload.objectName || payload.TableName || payload.tableName;
        let objectType = payload.ObjectType || payload.objectType;
        let query = payload.SqlQuery || payload.sqlQuery;

        if (sourceDb && targetDb && objectName) {
            actionName = "Transfer Object";
        } else if (sourceDb && targetDb) {
            actionName = "Clone Schema/Data";
        } else if (query) {
            actionName = "Execute Query";
            if (query.toLowerCase().includes('truncate')) actionName = "Truncate Table";
            if (query.toLowerCase().includes('drop')) actionName = "Drop Object";
        } else if (targetDb && !sourceDb) {
            actionName = "Delete Database";
        }

        return (
            <div className="d-flex flex-column gap-1">
                <div className="fw-bold text-primary">{actionName}</div>
                {sourceDb && targetDb ? (
                    <div className="fs-9 text-body-secondary d-flex align-items-center gap-2 mt-1">
                        <span className="badge bg-light border text-dark"><FontAwesomeIcon icon={faDatabase} className="me-1 text-muted" /> {sourceDb}</span>
                        <FontAwesomeIcon icon={faArrowRight} className="text-muted" style={{ fontSize: '0.7rem' }} />
                        <span className="badge bg-light border text-dark"><FontAwesomeIcon icon={faDatabase} className="me-1 text-muted" /> {targetDb}</span>
                    </div>
                ) : targetDb ? (
                    <div className="fs-9 text-body-secondary mt-1">
                        <span className="badge bg-light border text-dark"><FontAwesomeIcon icon={faDatabase} className="me-1 text-muted" /> {targetDb}</span>
                    </div>
                ) : null}

                {objectName && (
                    <div className="mt-1 fs-9">
                        <span className="fw-semibold text-secondary">Object:</span> <span className="text-dark">{objectName}</span>
                        {objectType && <span className="ms-2 badge bg-secondary-subtle text-secondary">{objectType}</span>}
                    </div>
                )}

                {query && (
                    <div className="mt-1 bg-light border rounded p-1" style={{ fontSize: '0.75rem', fontFamily: 'monospace', maxHeight: '40px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {query.length > 30 ? query.substring(0, 30) + "..." : query}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="pt-2">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted fs-9 mb-0">These logs record all structural and data modifications performed on the databases.</p>
                <button className="btn btn-sm btn-outline-primary d-flex align-items-center" onClick={fetchHistory} disabled={loading}>
                    <FontAwesomeIcon icon={faSync} className={`me-2 ${loading ? 'fa-spin' : ''}`} /> Refresh
                </button>
            </div>

            <div className="table-responsive border rounded" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light sticky-top">
                        <tr>
                            <th width="5%" className="py-3 px-3 text-center fs-9 fw-bold text-body">No.</th>
                            <th width="12%" className="py-3 fs-9 fw-bold text-body">Action Type</th>
                            <th width="40%" className="py-3 fs-9 fw-bold text-body">Details</th>
                            <th width="15%" className="py-3 text-center fs-9 fw-bold text-body">Status</th>
                            <th width="28%" className="py-3 fs-9 fw-bold text-body">User & Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && logs.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center py-4">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                    <span className="ms-2 text-muted fs-9">Loading history...</span>
                                </td>
                            </tr>
                        ) : logs.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center py-4 text-muted fs-9">
                                    No history records found for Database Management.
                                </td>
                            </tr>
                        ) : (
                            logs.map((row, idx) => (
                                <tr key={row.Id || idx}>
                                    <td className="text-center px-3 fw-semibold text-muted fs-9">{idx + 1}</td>
                                    <td>{getActionBadge(row.ActionType)}</td>
                                    <td>{renderActionDetails(row)}</td>
                                    <td className="text-center">
                                        {row.IsSuccess ? (
                                            <div className="badge bg-success-subtle text-success px-2 py-1 fs-9 border border-success-subtle rounded-pill">
                                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Success
                                            </div>
                                        ) : (
                                            <Tooltip content={row.ErrorMessage || "Failed"}>
                                                <div className="badge bg-danger-subtle text-danger px-2 py-1 fs-9 border border-danger-subtle rounded-pill cursor-pointer">
                                                    <FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Failed
                                                </div>
                                            </Tooltip>
                                        )}
                                    </td>
                                    <td>
                                        <div className="d-flex flex-column">
                                            <span className="fw-semibold text-body-emphasis fs-9">{row.UserName || row.UserId}</span>
                                            <span className="text-body-tertiary" style={{ fontSize: '0.75rem' }}>
                                                {row.CreatedAt ? format(new Date(row.CreatedAt.endsWith('Z') ? row.CreatedAt : row.CreatedAt + 'Z'), 'yyyy-MM-dd hh:mm:ss a') : '-'}
                                            </span>
                                            <span className="text-muted" style={{ fontSize: '0.7rem' }}>IP: {row.IpAddress}</span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default HistoryTab;
